import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PhoneInput, useCountryFromIP } from '@/components/ui/phone-input';
import { ArrowRight, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useFormContext } from '@/hooks/useFormContext';
import FormContainer from './FormContainer';
import { consultationFormSchema, ConsultationFormData } from '@/lib/formSchemas';
import { serviceOptionsGrouped } from '@/lib/formConfig';
import { supabase } from '@/integrations/supabase/client';

/**
 * ConsultationForm - Primary form used across most pages
 * 
 * Features:
 * - Context-aware service auto-selection
 * - IP-based country code detection for phone
 * - Clean, minimal design
 * - Inline validation
 * - Database storage via Supabase
 * - Email notification via edge function
 */

interface ConsultationFormProps {
  title?: string;
  description?: string;
  className?: string;
  showCalendly?: boolean;
}

const ConsultationForm = ({
  title = "Start a conversation",
  description = "Tell us about your growth challenges. We will respond within one business day.",
  className = '',
  showCalendly = false,
}: ConsultationFormProps) => {
  const { toast } = useToast();
  const { detectedService, shouldAutoSelect } = useFormContext();
  const { countryCode: detectedCountryCode, isLoading: isDetectingCountry } = useCountryFromIP('+1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wantsAssessment, setWantsAssessment] = useState(false);

  const form = useForm<ConsultationFormData>({
    resolver: zodResolver(consultationFormSchema),
    defaultValues: {
      fullName: '',
      workEmail: '',
      countryCode: '+1',
      phoneNumber: '',
      service: detectedService || '',
      message: '',
    },
  });

  // Update country code when IP detection completes
  useEffect(() => {
    if (!isDetectingCountry && detectedCountryCode) {
      form.setValue('countryCode', detectedCountryCode);
    }
  }, [detectedCountryCode, isDetectingCountry, form]);

  const onSubmit = async (data: ConsultationFormData) => {
    setIsSubmitting(true);
    
    try {
      // Store in database
      const { error: dbError } = await supabase
        .from('contacts')
        .insert({
          full_name: data.fullName,
          email: data.workEmail,
          country_code: data.countryCode,
          phone_number: data.phoneNumber,
          message: data.message || '',
          service_interest: data.service || null,
          assessment_requested: wantsAssessment,
        });

      if (dbError) {
        console.error('Database error:', dbError);
        throw new Error('Failed to save contact');
      }

      // Send email notification via edge function
      try {  
        await supabase.functions.invoke('send-contact-notification', {
          body: {
            fullName: data.fullName,
            email: data.workEmail,
            countryCode: data.countryCode,
            phoneNumber: data.phoneNumber,
            service: data.service,
            message: data.message,
            wantsAssessment: wantsAssessment,
          },
        });
      } catch (emailError) {
        // Don't fail the submission if email fails
        console.error('Email notification error:', emailError);
      }

      toast({
        title: "Message sent",
        description: "We will be in touch within one business day.",
      });
      
      form.reset();
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer title={title} description={description} className={className}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Smith" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="workEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Work Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@company.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Phone Number with Country Code */}
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number *</FormLabel>
                <FormControl>
                  <PhoneInput
                    value={field.value}
                    onChange={field.onChange}
                    countryCode={form.watch('countryCode')}
                    onCountryCodeChange={(code) => form.setValue('countryCode', code)}
                    placeholder="Enter your phone number"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="service"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Interested In *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-background border border-border z-50 max-h-[300px]">
                    {Object.entries(serviceOptionsGrouped).map(([category, services]) => (
                      <SelectGroup key={category}>
                        <SelectLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5">
                          {category}
                        </SelectLabel>
                        {services.map((service) => (
                          <SelectItem key={service} value={service}>
                            {service}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                {shouldAutoSelect && (
                  <FormDescription className="text-xs text-muted-foreground">
                    Pre-selected based on the page you visited. You can change this.
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="What's currently limiting growth, or what prompted you to reach out?"
                    className="min-h-[120px] resize-none"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Optional assessment checkbox */}
          <div className="flex items-start space-x-3">
            <Checkbox
              id="assessment"
              checked={wantsAssessment}
              onCheckedChange={(checked) => setWantsAssessment(checked === true)}
              className="mt-0.5"
            />
            <label
              htmlFor="assessment"
              className="text-sm text-muted-foreground leading-tight cursor-pointer"
            >
              I'd like a brief, written assessment after this conversation
            </label>
          </div>

          <div className="space-y-3">
            <Button 
              type="submit" 
              size="lg" 
              className="group w-full md:w-auto"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Start a serious conversation'}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <p className="text-xs text-muted-foreground">
              No pitches. No junior handoff. You'll speak with a strategist.
            </p>
          </div>

          {/* Secondary Calendly CTA */}
          {showCalendly && (
            <div className="pt-6 mt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3">
                Prefer to speak directly?
              </p>
              <a
                href="https://calendly.com/shyam-xperfomax/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book a 30-minute strategy call
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          )}
        </form>
      </Form>
    </FormContainer>
  );
};

export default ConsultationForm;
